import { type ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { LoadingSpinner } from '../common/LoadingSpinner'
import { SaAlert, SaButton } from '../../features/superadmin/design-system/components'
import { useSuperadminMfaGate } from '../../features/superadmin/hooks/useSuperadminMfaGate'
import { SuperadminThemeProvider } from '../../features/superadmin/layout/SuperadminThemeProvider'
import { MfaChallengeScreen } from '../../features/superadmin/security/MfaChallengeScreen'
import {
  isImpersonationViewPath,
  markMfaSessionBridge,
} from '../../features/superadmin/security/mfaBridge.utils'

interface RequireSuperadminProps {
  children: ReactNode
}

const MFA_SETUP_PATH = '/superadmin/security'

function isMfaSetupRoute (pathname: string): boolean {
  return pathname === MFA_SETUP_PATH || pathname.startsWith(`${MFA_SETUP_PATH}/`)
}

export function RequireSuperadmin ({ children }: RequireSuperadminProps) {
  const { user, loading, profileLoading, isSuperadmin } = useAuth()
  const location = useLocation()
  const isImpersonationView = isImpersonationViewPath(location.pathname)
  const { status, loading: mfaLoading, error: mfaError, reload } = useSuperadminMfaGate({
    allowBridgeRetry: isImpersonationView,
  })

  useEffect(() => {
    if (status?.isSessionElevated) {
      markMfaSessionBridge()
    }
  }, [status?.isSessionElevated])

  function handleMfaVerified () {
    markMfaSessionBridge()
    void reload()
  }

  if (loading || profileLoading || mfaLoading) return <LoadingSpinner />
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />
  if (!isSuperadmin) return <Navigate to="/login" replace />

  if (mfaError) {
    return (
      <SuperadminThemeProvider>
        <div className="flex min-h-dvh items-center justify-center p-4">
          <div className="w-full max-w-md space-y-4">
            <SaAlert variant="danger" title="2FA-controle mislukt" description={mfaError} />
            <SaButton type="button" variant="secondary" size="sm" onClick={() => void reload()}>
              Opnieuw proberen
            </SaButton>
          </div>
        </div>
      </SuperadminThemeProvider>
    )
  }

  if (!status?.hasVerifiedTotp) {
    if (isMfaSetupRoute(location.pathname)) {
      return <>{children}</>
    }
    return (
      <Navigate
        to={`${MFA_SETUP_PATH}?mfa=required`}
        replace
        state={{ from: location }}
      />
    )
  }

  if (!status.isSessionElevated) {
    const factorId = status.verifiedFactor?.id
    if (!factorId) {
      return <Navigate to={`${MFA_SETUP_PATH}?mfa=required`} replace />
    }

    return (
      <SuperadminThemeProvider>
        <MfaChallengeScreen
          factorId={factorId}
          purpose={isImpersonationView ? 'impersonation' : 'superadmin'}
          onVerified={handleMfaVerified}
        />
      </SuperadminThemeProvider>
    )
  }

  return <>{children}</>
}
